import { RRule } from 'rrule';

import { DateUtils } from '@shared/utils/date/date.utils';
import {
  VisibilityRecurrenceRulePayload,
  VisibilityRecurrenceSchedulePayload,
} from './visibility-recurrence.models';
import { normalizeRecurrenceTimeSlots } from './visibility-recurrence.normalize';

const TIME_RE = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Default RRULE anchor when no date is provided (matches previous behavior: local 1997-09-02 09:00). */
const DEFAULT_DTSTART = new Date(1997, 8, 2, 9, 0, 0, 0);

/**
 * Evaluates recurrence + time window for visibility (domain service).
 * Order: optional schedule bounds → recurrence on marketplace calendar day → time-of-day window.
 */
export class VisibilityRecurrenceScheduleEvaluator {
  matches(at: Date, schedule: VisibilityRecurrenceSchedulePayload): boolean {
    if (!schedule?.rules?.length) {
      return false;
    }

    if (!this.isWithinScheduleBounds(at, schedule)) {
      return false;
    }

    return schedule.rules.some(rule => this.ruleMatches(at, rule, schedule));
  }

  private isWithinScheduleBounds(at: Date, schedule: VisibilityRecurrenceSchedulePayload): boolean {
    const start = this.parseIsoDateOnlyScheduleStart(schedule.scheduleStart);
    const endInclusive = this.parseIsoDateOnlyScheduleEnd(schedule.scheduleEnd);

    if (start) {
      const dayStart = DateUtils.dayjsInMarketplaceTimezone(at).startOf('day').toDate().getTime();
      if (dayStart < start.getTime()) {
        return false;
      }
    }

    if (endInclusive) {
      if (at.getTime() > endInclusive.getTime()) {
        return false;
      }
    }

    return true;
  }

  private ruleMatches(
    at: Date,
    rule: VisibilityRecurrenceRulePayload,
    schedule: VisibilityRecurrenceSchedulePayload,
  ): boolean {
    const rrule = this.buildRRule(rule, schedule);
    if (!rrule) {
      return false;
    }

    if (!this.occursOnMarketplaceDay(rrule, at)) {
      return false;
    }

    const slots = normalizeRecurrenceTimeSlots(rule);
    if (slots.length === 0) {
      return false;
    }

    return slots.some(slot => this.isWithinTimeWindow(at, slot.startTime, slot.endTime));
  }

  private buildRRule(
    rule: VisibilityRecurrenceRulePayload,
    schedule: VisibilityRecurrenceSchedulePayload,
  ): RRule | null {
    const body = this.normalizeRruleBody(rule.rrule);
    if (!body) {
      return null;
    }

    try {
      const partial = RRule.parseString(body);
      const dtstart =
        this.parseIsoDateOnlyRruleAnchor(rule.dtstart) ??
        this.parseIsoDateOnlyRruleAnchor(schedule.scheduleStart) ??
        DEFAULT_DTSTART;

      return new RRule({
        ...partial,
        dtstart,
      });
    } catch {
      return null;
    }
  }

  private occursOnMarketplaceDay(rule: RRule, at: Date): boolean {
    const from = DateUtils.dayjsInMarketplaceTimezone(at).startOf('day').toDate();
    const to = DateUtils.dayjsInMarketplaceTimezone(at).endOf('day').toDate();
    const hits = rule.between(from, to, true);
    return hits.length > 0;
  }

  private isWithinTimeWindow(at: Date, startTime: string, endTime: string): boolean {
    const atInMarketplace = DateUtils.dayjsInMarketplaceTimezone(at);
    const currentM = atInMarketplace.hour() * 60 + atInMarketplace.minute();
    const hasStart = this.isValidTime(startTime);
    const hasEnd = this.isValidTime(endTime);

    if (!hasStart && !hasEnd) {
      return true;
    }

    if (hasStart && !hasEnd) {
      return currentM >= this.timeToMinutes(startTime);
    }

    if (!hasStart && hasEnd) {
      return currentM <= this.timeToMinutes(endTime);
    }

    const startM = this.timeToMinutes(startTime);
    const endM = this.timeToMinutes(endTime);

    if (endM < startM) {
      return currentM >= startM || currentM <= endM;
    }

    return currentM >= startM && currentM <= endM;
  }

  private normalizeRruleBody(raw: string): string | null {
    if (!raw || typeof raw !== 'string') {
      return null;
    }
    let s = raw.trim();
    if (!s) {
      return null;
    }
    if (/^rrule:/i.test(s)) {
      s = s.replace(/^rrule:/i, '').trim();
    }
    return s;
  }

  /**
   * YYYY-MM-DD as start of that civil day in marketplace timezone (aligns with `time` in visibility context).
   */
  private parseIsoDateOnlyScheduleStart(value: string | null | undefined): Date | null {
    if (!this.isIsoDateOnlyString(value)) {
      return null;
    }
    const d = DateUtils.dayjsInMarketplaceTimezone(value.trim()).startOf('day').toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /**
   * YYYY-MM-DD as end of that civil day in marketplace timezone (inclusive upper bound).
   */
  private parseIsoDateOnlyScheduleEnd(value: string | null | undefined): Date | null {
    if (!this.isIsoDateOnlyString(value)) {
      return null;
    }
    const d = DateUtils.dayjsInMarketplaceTimezone(value.trim()).endOf('day').toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /**
   * YYYY-MM-DD as UTC midnight for the calendar components. Used only as RRULE `dtstart` anchor so
   * `BYDAY` / `FREQ` math matches `rrule` (which keys weekday boundaries to the UTC instant).
   */
  private parseIsoDateOnlyRruleAnchor(value: string | null | undefined): Date | null {
    if (!this.isIsoDateOnlyString(value)) {
      return null;
    }
    const m = ISO_DATE_RE.exec(value.trim());
    if (!m) {
      return null;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(Date.UTC(y, mo, d, 0, 0, 0, 0));
  }

  private isIsoDateOnlyString(value: string | null | undefined): value is string {
    return typeof value === 'string' && ISO_DATE_RE.test(value.trim());
  }

  private timeToMinutes(time: string): number {
    const parts = time.split(':');
    const h = parts[0] ? Number(parts[0]) : 0;
    const m = parts[1] ? Number(parts[1]) : 0;
    return h * 60 + m;
  }

  private isValidTime(time: string): boolean {
    return typeof time === 'string' && TIME_RE.test(time);
  }
}
